import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import Modal from 'react-modal';
import { FiChevronLeft, FiChevronRight, FiCamera, FiGrid, FiClock } from 'react-icons/fi';
import { FaUmbrellaBeach, FaBed, FaWalking, FaHome } from 'react-icons/fa';
import '../../styles/WorkTimeReportModal.css';
import '../../styles/newDesign.css';
import { useNdTheme } from '../../utils/ndTheme';
import AppUsageModal from './AppUsageModal';
import RemoteWorktimeReportModal from './RemoteWorktimeReportModal';
import UserWorkTimeDetailsMobile from '../UserWorkTimeDetailsMobile';
import OneCDocumentHistoryMobile from '../OneCDocumentHistoryMobile';
import {
  LATIN_TO_CYRILLIC_FIO,
  EXPECTED_EMPLOYEES_FOR_ATTENDANCE,
  toCyrillicFio,
  matchesLocalReportSearch,
  localReportSearchLabels,
} from '../../utils/latinToCyrillicFio';
import { hideDuplicateFioRows, countHiddenFioDuplicates } from '../../utils/hideDuplicateFioRows';
import { loadFrontendSettings } from '../../utils/frontendSettings';
import { getApiBaseURL, parseFetchJsonResponse } from '../../utils/apiUrl';
import WorkTimeHub from '../WorkTimeHub';

Modal.setAppElement('#root');

// Все запросы к API через базовый URL (чтобы при клиенте на :3001 уходили на сервер :5000)
const api = (path) => `${getApiBaseURL()}${path.startsWith('/') ? '' : '/'}${path}`;

// Увеличиваем таймаут загрузки отчёта
const REPORT_FETCH_TIMEOUT_MS = 180000; // 180 сек (3 минуты) — если дольше, показываем ошибку

function fetchWithTimeout(url, options = {}, timeoutMs = REPORT_FETCH_TIMEOUT_MS) {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);
  return fetch(url, { ...options, signal: controller.signal }).then(
    (r) => {
      clearTimeout(timeout);
      return r;
    },
    (e) => {
      clearTimeout(timeout);
      throw e;
    }
  );
}

// Норма рабочего дня для шкалы «Отработано»
const ND_WORK_DAY_MINUTES = 480;
const ND_AVATAR_COLORS = ['#2dd4bf', '#22d3ee', '#38bdf8', '#fbbf24', '#f0566a', '#34d399', '#a78bfa', '#f97316'];

function ndToYmd(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function ndFormatYmd(ymd) {
  if (!ymd) return '';
  const [y, m, d] = String(ymd).split('-');
  return `${d}.${m}.${y}`;
}

/** Границы периода (день / неделя с понедельника / календарный месяц), обрезанные сегодняшним днём. */
function ndRangeBounds(anchorYmd, range) {
  const anchor = new Date(`${anchorYmd}T00:00:00`);
  if (Number.isNaN(anchor.getTime())) return [anchorYmd, anchorYmd];
  let start = anchor;
  let end = anchor;
  if (range === 'week') {
    const shift = (anchor.getDay() + 6) % 7;
    start = new Date(anchor);
    start.setDate(anchor.getDate() - shift);
    end = new Date(start);
    end.setDate(start.getDate() + 6);
  } else if (range === 'month') {
    start = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    end = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
  }
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (end > today) end = today;
  if (start > end) start = end;
  return [ndToYmd(start), ndToYmd(end)];
}

function ndInitials(name) {
  const clean = String(name || '').trim();
  if (!clean) return '?';
  const parts = clean.split(/\s+/).filter(Boolean);
  const letters = parts.length > 1 ? parts[0][0] + parts[1][0] : clean.slice(0, 2);
  return letters.toUpperCase();
}

function paintLocalReportFio(row, hovered) {
  const name = row && row.querySelector('.nd-whoName');
  if (!name) return;
  if (!hovered) {
    name.style.removeProperty('color');
    name.style.removeProperty('-webkit-text-fill-color');
    return;
  }
  const apply = () => {
    const timeTd = row.querySelector('td.nd-tC');
    const fromTime = timeTd ? getComputedStyle(timeTd).color : '';
    const fromVar = getComputedStyle(document.documentElement).getPropertyValue('--table-cell-hover-text-color').trim();
    const color = fromTime || fromVar || '#ffffff';
    name.style.setProperty('color', color, 'important');
    name.style.setProperty('-webkit-text-fill-color', color, 'important');
  };
  apply();
  requestAnimationFrame(apply);
}

// Вынесем парсер вне функции
function parseDate(str) {
  if (!str) return null;
  
  // СНАЧАЛА проверяем ISO-формат с Z (UTC) - это приоритет
  // Это важно, чтобы не парсить UTC время как локальное
  if (/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}.*Z/.test(str)) {
    return new Date(str); // new Date() автоматически конвертирует UTC в локальное время
  }
  
  // Если YYYY-MM-DD HH:mm:ss или YYYY-MM-DDTHH:mm:ss (локальное время без часового пояса)
  // НО только если нет Z в конце (чтобы не перехватить UTC формат)
  if (!str.endsWith('Z')) {
    const matchYmd = str.match(/(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})/);
    if (matchYmd) {
      const [, yyyy, mm, dd, hh, min, ss] = matchYmd;
      // В JS месяцы с 0, поэтому -1, создаем как локальное время
      return new Date(Number(yyyy), Number(mm) - 1, Number(dd), Number(hh), Number(min), Number(ss));
    }
  }
  // Если DD.MM.YYYY HH:mm:ss
    const match = str.match(/(\d{2})\.(\d{2})\.(\d{4})[ T](\d{2}):(\d{2}):(\d{2})/);
    if (match) {
      const [, dd, mm, yyyy, hh, min, ss] = match;
    // В JS месяцы с 0, поэтому -1
    return new Date(Number(yyyy), Number(mm) - 1, Number(dd), Number(hh), Number(min), Number(ss));
  }
  // Если только дата без времени
    const matchDate = str.match(/(\d{2})\.(\d{2})\.(\d{4})/);
    if (matchDate) {
      const [, dd, mm, yyyy] = matchDate;
    return new Date(Number(yyyy), Number(mm) - 1, Number(dd));
  }
  // Падение в стандартный парсер
  return new Date(str);
}

// eslint-disable-next-line no-unused-vars
function calculateWorkTime(logs, startDate, endDate) {
  const userMap = {};
  logs.forEach(log => {
    if (!userMap[log.username]) userMap[log.username] = [];
    userMap[log.username].push(log);
  });

  return Object.entries(userMap).map(([username, userLogs]) => {
    // Фильтруем только по дате (без времени), чтобы не было ошибок из-за формата
    const filtered = userLogs.filter(l => {
      const t = parseDate(l.event_time);
      const tDate = t.toISOString().slice(0, 10);
      return tDate >= startDate && tDate <= endDate;
    });

    if (filtered.length === 0) {
      return {
        username,
        firstLogin: '',
        lastLogout: '',
        workMinutes: 0,
        logs: []
      };
    }

    // Сортируем по времени
    const sorted = filtered.slice().sort((a, b) => parseDate(a.event_time) - parseDate(b.event_time));

    // Первый вход — только event_type 'login' (трекер ПК). Записи активности (other/activity) не считаем входом.
    const logins = sorted.filter(l => l.event_type === 'login');
    const firstLogin = logins.length > 0 ? logins.reduce((min, l) => parseDate(l.event_time) < parseDate(min.event_time) ? l : min) : null;

    // Последний выход — событие с event_type 'logout'
    const logouts = sorted.filter(l => l.event_type === 'logout');
    const lastLogout = logouts.length > 0 ? logouts.reduce((max, l) => parseDate(l.event_time) > parseDate(max.event_time) ? l : max) : null;

    // Для отображения: если нет выхода, показываем последний login
    let displayLastLogout = lastLogout ? lastLogout.event_time : '';
    if (!displayLastLogout && logins.length > 0) {
      displayLastLogout = logins[logins.length - 1].event_time;
    }

    let workMinutes = 0;
    if (firstLogin && displayLastLogout) {
      // Правильно парсим UTC время для вычисления разницы
      const loginDate = parseDate(firstLogin.event_time);
      const logoutDate = parseDate(displayLastLogout);
      
      if (loginDate && logoutDate) {
        workMinutes = Math.round((logoutDate.getTime() - loginDate.getTime()) / 60000);
        
        // Логируем для отладки
        console.log('[calculateWorkTime] Вычисление времени работы:', {
          username,
          firstLogin: firstLogin.event_time,
          lastLogout: displayLastLogout,
          loginDateUTC: loginDate.toISOString(),
          logoutDateUTC: logoutDate.toISOString(),
          loginDateLocal: loginDate.toString(),
          logoutDateLocal: logoutDate.toString(),
          diffMs: logoutDate.getTime() - loginDate.getTime(),
          workMinutes
        });
      }
    }

    // Отладочный вывод
    console.log('User:', username, 'First login:', firstLogin ? firstLogin.event_time : 'none', 'Last logout:', displayLastLogout, 'Work minutes:', workMinutes);

    return {
      username,
      firstLogin: firstLogin ? firstLogin.event_time : '',
      lastLogout: displayLastLogout,
      workMinutes,
      logs: sorted
    };
  });
}

function WorkTimeReportModal({ isOpen, onRequestClose, open, onClose }) {
  const actuallyOpen = isOpen ?? open;
  const handleRequestClose = onRequestClose ?? onClose;
  const [showAppUsage, setShowAppUsage] = useState(false);
  const [showRemoteWorktime, setShowRemoteWorktime] = useState(false);
  const [showLocalReport, setShowLocalReport] = useState(false);
  const [showOneCHistoryModal, setShowOneCHistoryModal] = useState(false);

  // Состояния для модалки локального отчета
  // По умолчанию устанавливаем сегодняшнюю дату (используем локальное время, а не UTC)
  const getTodayDate = () => {
    const d = new Date();
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  const [localReportCurrentDate, setLocalReportCurrentDate] = useState(getTodayDate());
  const [localReportStartDate, setLocalReportStartDate] = useState(getTodayDate());
  const [localReportEndDate, setLocalReportEndDate] = useState(getTodayDate());
  // Период отчёта: день / неделя / месяц (чипы «Сегодня — Неделя — Месяц»)
  const [localReportRange, setLocalReportRange] = useState('day');
  const [localReportSort, setLocalReportSort] = useState({ k: 'i', d: 1 });
  const [hideFioDuplicates, setHideFioDuplicates] = useState(() => {
    try {
      return localStorage.getItem('local-report-hide-fio-dups') !== '0';
    } catch (_) {
      return true;
    }
  });
  const [nowTick, setNowTick] = useState(() => new Date());
  const { isLight: ndIsLight, toggle: toggleNdTheme } = useNdTheme();
  const [localReportSearchTerm, setLocalReportSearchTerm] = useState('');
  const [localReportUsers, setLocalReportUsers] = useState([]);
  const [localReportUsersList, setLocalReportUsersList] = useState([]);
  const [localReportUserOptions, setLocalReportUserOptions] = useState([]);
  const [localReportShowAutocomplete, setLocalReportShowAutocomplete] = useState(false);
  const [loadingLocalReport, setLoadingLocalReport] = useState(false);
  const [localReportError, setLocalReportError] = useState(null);
  const [localReportDetailsModal, setLocalReportDetailsModal] = useState({
    open: false,
    logs: [],
    username: '',
    realUsername: '',
    activityStats: null,
    urls: [],
    applications: [],
    screenshots: [],
    startDate: '',
    endDate: ''
  });
  const [hoveredLocalRowIdx, setHoveredLocalRowIdx] = useState(null);
  // Если в столбце «Первый вход» нет данных, подставляем время первого приложения из модалки «Подробнее» (вкладка Приложения). Ключ: "username-start-end"
  const [firstLoginFromApplications, setFirstLoginFromApplications] = useState({});
  // Если в столбце «Последний выход» нет данных, подставляем время последнего приложения за день из вкладки Приложения.
  const [lastLogoutFromApplications, setLastLogoutFromApplications] = useState({});
  // Флаги «нет данных» по вкладкам (скриншоты, приложения) для отображения перечёркнутых иконок. Ключ: "username-start-end"
  const [detailsAvailability, setDetailsAvailability] = useState({});
  // Данные календаря (отпуска, отгулы, больничные, удалёнка) для причин отсутствия
  const [calendarLeaves, setCalendarLeaves] = useState([]);
  const [calendarUsersById, setCalendarUsersById] = useState({});
  // Переопределения имён для отчёта локальных ПК (латиница → кириллица, флаг «скрывать из Отсутствуют»)
  const [fioAliases, setFioAliases] = useState({});
  const [fioAliasesLoading, setFioAliasesLoading] = useState(false);
  const [fioAliasesError, setFioAliasesError] = useState(null);
  const [editingAliasLogin, setEditingAliasLogin] = useState('');
  const [editingAliasDisplayName, setEditingAliasDisplayName] = useState('');
  const [editingAliasHide, setEditingAliasHide] = useState(false);
  // Системное сообщение Windows (msg *): показ поля ввода и отправка
  const [showSystemMessageInput, setShowSystemMessageInput] = useState(false);
  const [systemMessageText, setSystemMessageText] = useState('');
  const [systemMessageTarget, setSystemMessageTarget] = useState('all'); // 'all' | username
  const [windowsSessions, setWindowsSessions] = useState([]);
  const [windowsSessionsLoading, setWindowsSessionsLoading] = useState(false);
  const [systemMessageSending, setSystemMessageSending] = useState(false);
  const [systemMessageError, setSystemMessageError] = useState(null);
  const [showSessionsListOpen, setShowSessionsListOpen] = useState(false);
  const sessionsListRef = useRef(null);
  // Формат отображения сессии: имя + фамилия (displayName + sessionName из диспетчера задач)
  const formatSessionDisplay = useCallback((u) => {
    if (typeof u !== 'object') return String(u || '');
    const name = (u.displayName || u.username || '').trim();
    const sn = (u.sessionName && String(u.sessionName).trim()) || '';
    return sn ? `${name} ${sn}`.trim() : name;
  }, []);
  // Кэш отчёта за сегодня: при открытии родительской модалки подгружаем данные заранее
  const prefetchedLocalReport = useRef({ date: null, report: null });
  // Цвет заголовка: приоритет — переменная для мониторинга (из «Отчеты мониторинга») → глобальная → настройки
  const readHeaderColor = useCallback(() => {
    if (typeof document === 'undefined' || !document.documentElement) return '#60a5fa';
    const monitoringColor = getComputedStyle(document.documentElement).getPropertyValue('--modal-monitoring-title-color').trim();
    if (monitoringColor) return monitoringColor;
    const defaultColor = getComputedStyle(document.documentElement).getPropertyValue('--modal-default-header-text-color').trim();
    if (defaultColor) return defaultColor;
    const fs = loadFrontendSettings() || {};
    return fs.modals?.monitoring?.titleColor ?? fs.modalHeaderTextColor ?? '#60a5fa';
  }, []);
  const [headerColor, setHeaderColor] = useState(() => readHeaderColor());

  // Справочник «первое слово ФИО» → полное ФИО (как в «Отсутствуют в отчёте») для подстановки в списке получателей
  const firstWordToFullFio = useMemo(() => {
    const m = new Map();
    Object.values(LATIN_TO_CYRILLIC_FIO).forEach((fio) => {
      const first = (fio || '').trim().split(/\s+/)[0];
      if (first) m.set(first.toLowerCase(), fio);
    });
    return m;
  }, []);

  const resolveToFullFio = useCallback((name) => {
    if (!name || typeof name !== 'string') return name || '';
    const trimmed = name.trim();
    if (trimmed.split(/\s+/).length > 1) return trimmed;
    return firstWordToFullFio.get(trimmed.toLowerCase()) || trimmed;
  }, [firstWordToFullFio]);

  const fioAliasesMap = useMemo(() => fioAliases || {}, [fioAliases]);

  const resolveFio = useCallback(
    (latin) => {
      if (!latin || typeof latin !== 'string') return latin || '';
      const alias = fioAliasesMap[latin];
      if (alias && alias.display_name) return alias.display_name;
      return toCyrillicFio(latin);
    },
    [fioAliasesMap]
  );

  useEffect(() => {
    setHeaderColor(readHeaderColor());
  }, [actuallyOpen, readHeaderColor]);

  useEffect(() => {
    const h = () => setHeaderColor(readHeaderColor());
    window.addEventListener('frontendSettingsUpdated', h);
    return () => window.removeEventListener('frontendSettingsUpdated', h);
  }, [readHeaderColor]);

  useEffect(() => {
    document.documentElement.style.setProperty('--worktime-header-color', headerColor);
    return () => { document.documentElement.style.removeProperty('--worktime-header-color'); };
  }, [headerColor]);

  // Сбрасываем состояние дочерних модалок при закрытии родительской
  useEffect(() => {
    console.log('🔍 [WorkTimeReportModal] useEffect actuallyOpen изменился:', actuallyOpen);
    if (!actuallyOpen) {
      console.log('🔴 [WorkTimeReportModal] Закрываем родительскую модалку, сбрасываем дочерние состояния');
      setShowAppUsage(false);
      setShowRemoteWorktime(false);
      setShowLocalReport(false);
      setShowOneCHistoryModal(false);
    } else {
      // При открытии родительской модалки также сбрасываем дочерние
      console.log('🟢 [WorkTimeReportModal] Открываем родительскую модалку, сбрасываем дочерние состояния');
      setShowAppUsage(false);
      setShowRemoteWorktime(false);
      setShowLocalReport(false);
      setShowOneCHistoryModal(false);
    }
  }, [actuallyOpen]);

  // Префетч отчёта за сегодня при открытии родительской модалки (чтобы при клике «Отчет активности локальных ПК» данные уже были)
  useEffect(() => {
    if (!actuallyOpen) return;
    const today = getTodayDate();
    fetch(api(`/api/local-worktime-report?start=${today}&end=${today}`))
      .then((res) => parseFetchJsonResponse(res))
      .then(data => {
        if (data.success && Array.isArray(data.report)) {
          prefetchedLocalReport.current = { date: today, report: data.report };
        }
      })
      .catch(() => {});
  }, [actuallyOpen]);

  // Загрузка списка пользователей
  useEffect(() => {
    if (showLocalReport) {
      fetch(api('/api/worktime-users'))
        .then(res => res.json())
        .then(data => {
          setLocalReportUsersList(data.users || []);
          setLocalReportUserOptions(data.users || []);
        })
        .catch(() => {
          setLocalReportUsersList([]);
          setLocalReportUserOptions([]);
        });
    }
  }, [showLocalReport]);

  // Загрузка переопределений имён для отчёта локальных ПК
  useEffect(() => {
    if (!showLocalReport) return;
    setFioAliasesLoading(true);
    setFioAliasesError(null);
    fetch(api('/api/local-worktime-aliases'))
      .then((res) => res.json())
      .then((data) => {
        if (data && data.success && Array.isArray(data.aliases)) {
          const map = {};
          data.aliases.forEach((row) => {
            if (row && row.login_latin) {
              map[row.login_latin] = {
                display_name: row.display_name || '',
                hide_in_absent: !!row.hide_in_absent,
                deleted: !!row.deleted,
              };
            }
          });
          setFioAliases(map);
        } else {
          setFioAliases({});
        }
      })
      .catch((err) => {
        console.error('❌ [WorkTimeReportModal] Ошибка загрузки local-worktime-aliases:', err);
        setFioAliases({});
        setFioAliasesError(err?.message || 'Ошибка загрузки настроек имён');
      })
      .finally(() => setFioAliasesLoading(false));
  }, [showLocalReport]);

  // Загрузка данных календаря (отпуска, отгулы, больничные, удалёнка) для блока «Отсутствуют»
  useEffect(() => {
    if (!showLocalReport) return;
    const token = localStorage.getItem('token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    Promise.all([
      fetch(api('/api/all-leaves'), { headers }).then(r => r.json()).then(d => Array.isArray(d) ? d : []),
      fetch(api('/api/users'), { headers }).then(r => r.json()).then(d => {
        const arr = Array.isArray(d) ? d : (d?.users ? d.users : []);
        return Object.fromEntries((arr || []).map(u => [u.id, u]));
      })
    ]).then(([leaves, usersById]) => {
      setCalendarLeaves(leaves);
      setCalendarUsersById(usersById || {});
    }).catch(() => {
      setCalendarLeaves([]);
      setCalendarUsersById({});
    });
  }, [showLocalReport]);

  const startEditAlias = useCallback(
    (loginLatin) => {
      if (!loginLatin) return;
      const login = String(loginLatin);
      const current = fioAliasesMap[login] || {};
      setEditingAliasLogin(login);
      setEditingAliasDisplayName(current.display_name || resolveFio(login));
      setEditingAliasHide(!!current.hide_in_absent);
      setFioAliasesError(null);
    },
    [fioAliasesMap, resolveFio]
  );

  const saveAlias = useCallback(() => {
    const loginLatin = (editingAliasLogin || '').trim();
    if (!loginLatin) return;
    const body = {
      loginLatin,
      displayName: (editingAliasDisplayName || '').trim(),
      hideInAbsent: editingAliasHide,
    };
    setFioAliasesLoading(true);
    setFioAliasesError(null);
    fetch(api('/api/local-worktime-aliases'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
      .then((res) => res.json())
      .then((data) => {
        if (!data || !data.success) {
          throw new Error(data?.error || 'Ошибка сохранения');
        }
        setFioAliases((prev) => ({
          ...prev,
          [loginLatin]: {
            ...prev[loginLatin],
            display_name: body.displayName,
            hide_in_absent: body.hideInAbsent,
          },
        }));
      })
      .catch((err) => {
        console.error('❌ [WorkTimeReportModal] Ошибка сохранения local-worktime-alias:', err);
        setFioAliasesError(err?.message || 'Ошибка сохранения настроек имён');
      })
      .finally(() => {
        setFioAliasesLoading(false);
      });
  }, [editingAliasLogin, editingAliasDisplayName, editingAliasHide]);

  // Удалить сотрудника из отчёта (deleted = true) или вернуть его (false). Логи активности не удаляются.
  const setAliasDeleted = useCallback((loginLatin, deleted) => {
    const login = (loginLatin || '').trim();
    if (!login) return;
    if (deleted && !window.confirm(`Удалить «${resolveFio(login)}» (${login}) из отчёта активности локальных ПК?\n\nДанные активности останутся в базе, сотрудника можно будет вернуть из списка «Удалённые из отчёта».`)) {
      return;
    }
    setFioAliasesLoading(true);
    setFioAliasesError(null);
    fetch(api('/api/local-worktime-aliases/deleted'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ loginLatin: login, deleted }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (!data || !data.success) {
          throw new Error(data?.error || 'Ошибка удаления');
        }
        setFioAliases((prev) => ({
          ...prev,
          [login]: { display_name: '', hide_in_absent: false, ...prev[login], deleted },
        }));
        if (deleted) {
          setEditingAliasLogin('');
          setEditingAliasDisplayName('');
          setEditingAliasHide(false);
        }
      })
      .catch((err) => {
        console.error('❌ [WorkTimeReportModal] Ошибка удаления сотрудника из отчёта:', err);
        setFioAliasesError(err?.message || 'Ошибка удаления сотрудника из отчёта');
      })
      .finally(() => {
        setFioAliasesLoading(false);
      });
  }, [resolveFio]);

  // Логины, удалённые из отчёта (в нижнем регистре — логины в логах и в списке сотрудников могут отличаться регистром)
  const deletedLoginsLower = useMemo(() => {
    const s = new Set();
    Object.entries(fioAliasesMap).forEach(([login, alias]) => {
      if (alias && alias.deleted) s.add(login.toLowerCase());
    });
    return s;
  }, [fioAliasesMap]);

  const deletedAliasLogins = useMemo(
    () => Object.keys(fioAliasesMap).filter((login) => fioAliasesMap[login]?.deleted).sort((a, b) => resolveFio(a).localeCompare(resolveFio(b), 'ru')),
    [fioAliasesMap, resolveFio]
  );
  const [showDeletedAliases, setShowDeletedAliases] = useState(false);
  // Вкладка под статистикой: 'online' — таблица присутствующих, 'offline' — «Отсутствуют в отчёте»
  const [localReportTab, setLocalReportTab] = useState('online');

  const fetchWindowsSessions = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    setWindowsSessionsLoading(true);
    fetch(api('/api/windows-sessions'), { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(data => {
        const list = (data && data.success && Array.isArray(data.users)) ? data.users : [];
        setWindowsSessions(list);
      })
      .catch(() => setWindowsSessions([]))
      .finally(() => setWindowsSessionsLoading(false));
  }, []);

  useEffect(() => {
    if (!showSystemMessageInput) return;
    fetchWindowsSessions();
  }, [showSystemMessageInput, fetchWindowsSessions]);

  useEffect(() => {
    if (!showSessionsListOpen) return;
    const onDocClick = (e) => {
      if (sessionsListRef.current && !sessionsListRef.current.contains(e.target)) setShowSessionsListOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [showSessionsListOpen]);

  // Навигация по дням стрелками для локального отчета
  // Применить выбранный период: обновляет якорную дату и границы отчёта.
  // Сама загрузка стартует в useEffect по изменению start/end.
  const applyLocalReportPeriod = useCallback((anchorYmd, range) => {
    const [start, end] = ndRangeBounds(anchorYmd, range);
    setLocalReportCurrentDate(anchorYmd);
    setLocalReportStartDate(start);
    setLocalReportEndDate(end);
  }, []);

  const shiftLocalReportPeriod = useCallback((direction) => {
    const anchor = new Date(`${localReportCurrentDate}T00:00:00`);
    if (Number.isNaN(anchor.getTime())) return;
    if (localReportRange === 'week') anchor.setDate(anchor.getDate() + 7 * direction);
    else if (localReportRange === 'month') anchor.setMonth(anchor.getMonth() + direction);
    else anchor.setDate(anchor.getDate() + direction);

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (anchor > today) {
      alert('Нельзя выбрать будущую дату');
      return;
    }
    applyLocalReportPeriod(ndToYmd(anchor), localReportRange);
  }, [localReportCurrentDate, localReportRange, applyLocalReportPeriod]);

  const handleLocalReportPreviousDay = () => shiftLocalReportPeriod(-1);
  const handleLocalReportNextDay = () => shiftLocalReportPeriod(1);

  // Загрузка отчета при изменении дат (использует префетч за сегодня, если есть). forceRefresh = true — всегда запрос на сервер (кнопка «Показать отчет», автообновление).
  // silent — фоновое автообновление: таблицу не прячем за «Загрузка…» и при ошибке не очищаем
  const fetchLocalReport = React.useCallback(async (forceRefresh = false, silent = false) => {
    if (!localReportStartDate || !localReportEndDate) return;
    const cache = prefetchedLocalReport.current;
    if (!forceRefresh && localReportStartDate === localReportEndDate && cache?.date === localReportStartDate && Array.isArray(cache?.report)) {
      setLocalReportUsers(cache.report);
      setLoadingLocalReport(false);
      setLocalReportError(null);
      return;
    }
    if (!silent) {
      setLoadingLocalReport(true);
      setLocalReportError(null);
    }
    try {
      const url = api(`/api/local-worktime-report?start=${localReportStartDate}&end=${localReportEndDate}`);
      const res = await fetchWithTimeout(url);
      const data = await parseFetchJsonResponse(res);
      if (data.success && Array.isArray(data.report)) {
        setLocalReportUsers(data.report);
        if (silent) setLocalReportError(null);
        if (localReportStartDate === localReportEndDate) {
          prefetchedLocalReport.current = { date: localReportStartDate, report: data.report };
        }
      } else if (!silent) {
        setLocalReportUsers([]);
      }
    } catch (error) {
      console.error('Ошибка загрузки локального отчета:', error);
      if (silent) return;
      setLocalReportUsers([]);
      setLocalReportError(error.name === 'AbortError'
        ? 'Загрузка прервана по таймауту (45 сек). Проверьте соединение с сервером или попробуйте позже.'
        : (error.message || 'Ошибка загрузки'));
    }
    setLoadingLocalReport(false);
  }, [localReportStartDate, localReportEndDate]);

  // Автоматически обновляем currentDate при изменении startDate через календарь (если startDate === endDate)
  useEffect(() => {
    if (localReportStartDate === localReportEndDate) {
      setLocalReportCurrentDate(localReportStartDate);
    }
  }, [localReportStartDate, localReportEndDate]);

  // Автозагрузка при открытии модалки и изменении дат
  useEffect(() => {
    if (showLocalReport && localReportStartDate && localReportEndDate) {
      fetchLocalReport();
    }
  }, [showLocalReport, localReportStartDate, localReportEndDate, fetchLocalReport]);

  // Автообновление отчёта раз в минуту (всегда с сервера, чтобы видеть новых сотрудников)
  useEffect(() => {
    if (!showLocalReport || !localReportStartDate || !localReportEndDate) return;
    const interval = setInterval(() => { fetchLocalReport(true, true); }, 60000);
    return () => clearInterval(interval);
  }, [showLocalReport, localReportStartDate, localReportEndDate, fetchLocalReport]);

  // Заполнить первый/последний вход из приложений из отчёта (чтобы колонка «Отработано» была корректна без открытия модалки «Подробнее»)
  useEffect(() => {
    if (!localReportUsers?.length || !localReportStartDate || !localReportEndDate) return;
    setFirstLoginFromApplications(prev => {
      const next = { ...prev };
      localReportUsers.forEach(row => {
        const key = `${row.username}-${localReportStartDate}-${localReportEndDate}`;
        if (row.firstLoginFromApplications) next[key] = row.firstLoginFromApplications;
      });
      return next;
    });
    setLastLogoutFromApplications(prev => {
      const next = { ...prev };
      localReportUsers.forEach(row => {
        const key = `${row.username}-${localReportStartDate}-${localReportEndDate}`;
        if (row.lastLogoutFromApplications) next[key] = row.lastLogoutFromApplications;
      });
      return next;
    });
  }, [localReportUsers, localReportStartDate, localReportEndDate]);

  const handleOpenLocalReport = () => {
    console.log('🔘 [WorkTimeReportModal] Кнопка "Отчет активности локальных ПК" нажата');
    console.log('🔘 [WorkTimeReportModal] showLocalReport до:', showLocalReport);
    setShowLocalReport(true);
    console.log('🔘 [WorkTimeReportModal] setShowLocalReport(true) вызван');
  };

  // Удаление данных за период дат
  const handleDeleteByDateRange = async (start, end) => {
    if (!start || !end) {
      alert('Выберите период дат для удаления');
      return;
    }
    
    if (!window.confirm(`Вы уверены, что хотите удалить все данные активности за период с ${start} по ${end}? Это действие нельзя отменить.`)) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(api('/api/activity-logs/clear'), {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ start, end })
      });
      
      const data = await res.json();
      
      if (res.ok && data.success) {
        alert(`✅ ${data.message || `Удалено ${data.deletedCount || 0} записей`}`);
        // Обновляем отчет после удаления
        fetchLocalReport();
      } else {
        alert(`❌ Ошибка: ${data.error || 'Не удалось удалить данные'}`);
      }
    } catch (error) {
      console.error('Ошибка удаления данных:', error);
      alert('❌ Ошибка при удалении данных');
    }
  };

  // Удаление данных за период (день/неделя/месяц)
  const handleDeleteByPeriod = async (period) => {
    const periodNames = {
      'day': 'сегодня',
      'week': 'последнюю неделю',
      'month': 'последний месяц'
    };
    if (!window.confirm(`Вы уверены, что хотите удалить все данные активности за ${periodNames[period] || period}? Это действие нельзя отменить.`)) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(api('/api/activity-logs/clear'), {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ period })
      });
      
      const data = await res.json();
      
      if (res.ok && data.success) {
        alert(`✅ ${data.message || `Удалено ${data.deletedCount || 0} записей`}`);
        // Обновляем отчет после удаления
        fetchLocalReport();
      } else {
        alert(`❌ Ошибка: ${data.error || 'Не удалось удалить данные'}`);
      }
    } catch (error) {
      console.error('Ошибка удаления данных:', error);
      alert('❌ Ошибка при удалении данных');
    }
  };

  // Обработка поиска
  const handleLocalReportSearchChange = (e) => {
    const value = e.target.value;
    setLocalReportSearchTerm(value);
    const labels = localReportSearchLabels(
      localReportUsers.length ? localReportUsers : localReportUsersList.map((u) => ({ fio: u, username: u })),
      resolveFio
    );
    if (value.length > 0) {
      setLocalReportShowAutocomplete(true);
      const q = value.toLowerCase();
      setLocalReportUserOptions(labels.filter((label) => label.toLowerCase().includes(q)));
    } else {
      setLocalReportShowAutocomplete(false);
      setLocalReportUserOptions(labels);
    }
  };

  // Фильтрация пользователей по поиску и датам
  const filteredLocalReportUsers = useMemo(() => {
    // Сотрудники, удалённые из отчёта, не показываются нигде (таблица, поиск, «Отсутствуют»)
    const visibleUsers = localReportUsers.filter((user) => !deletedLoginsLower.has((user.fio || '').toLowerCase()));
    if (!localReportStartDate || !localReportEndDate) {
      return visibleUsers;
    }

    // Создаем диапазон дат для фильтрации
    const startDate = new Date(localReportStartDate + 'T00:00:00');
    const endDate = new Date(localReportEndDate + 'T23:59:59');

    // Фильтруем пользователей и их sessions по датам
    const filtered = visibleUsers.map(user => {
      // Фильтруем sessions по датам
      const filteredSessions = (user.sessions || []).filter(session => {
        if (!session.event_time) return false;
        const sessionDate = parseDate(session.event_time);
        if (!sessionDate) return false;
        return sessionDate >= startDate && sessionDate <= endDate;
      });
      
      // Если нет sessions в диапазоне, но есть firstLogin или lastLogout в диапазоне, оставляем пользователя
      const hasLoginInRange = user.firstLogin && (() => {
        const loginDate = parseDate(user.firstLogin);
        return loginDate && loginDate >= startDate && loginDate <= endDate;
      })();
      
      const hasLogoutInRange = user.lastLogout && (() => {
        const logoutDate = parseDate(user.lastLogout);
        return logoutDate && logoutDate >= startDate && logoutDate <= endDate;
      })();
      
      // Если нет sessions, но есть firstLogin или lastLogout в диапазоне, оставляем пользователя
      if (filteredSessions.length === 0 && !hasLoginInRange && !hasLogoutInRange) {
        return null; // Исключаем пользователя
      }
      
      // Обновляем firstLogin и lastLogout на основе отфильтрованных sessions
      let newFirstLogin = '';
      let newLastLogout = '';
      let newTotalHours = 0;
      let newTotalTimeStr = '';
      
      if (filteredSessions.length > 0) {
        filteredSessions.sort((a, b) => new Date(a.event_time) - new Date(b.event_time));
        const firstLogin = filteredSessions.find(e => e.event_type === 'login');
        const lastLogout = [...filteredSessions].reverse().find(e => e.event_type === 'logout');

        newFirstLogin = firstLogin ? firstLogin.event_time : (hasLoginInRange ? user.firstLogin : '');
        newLastLogout = lastLogout ? lastLogout.event_time : (hasLogoutInRange ? user.lastLogout : '');

        if (firstLogin && lastLogout) {
          const loginTime = new Date(firstLogin.event_time);
          const logoutTime = new Date(lastLogout.event_time);
          const diffMs = logoutTime.getTime() - loginTime.getTime();

          console.log(`[WorkTimeReportModal] Вычисление времени работы:`, {
            firstLogin: firstLogin.event_time,
            lastLogout: lastLogout.event_time,
            loginTimeUTC: loginTime.toISOString(),
            logoutTimeUTC: logoutTime.toISOString(),
            loginTimeLocal: loginTime.toString(),
            logoutTimeLocal: logoutTime.toString(),
            diffMs,
            diffHours: diffMs / 3600000
          });

          const totalHours = Math.floor(diffMs / 3600000);
          const totalMinutes = Math.floor((diffMs % 3600000) / 60000);
          newTotalHours = Number((diffMs / 3600000).toFixed(1));
          newTotalTimeStr = `${totalHours} ч ${totalMinutes} мин`;
        }
      } else {
        // Если нет sessions, но есть firstLogin или lastLogout в диапазоне, используем их
        newFirstLogin = hasLoginInRange ? user.firstLogin : '';
        newLastLogout = hasLogoutInRange ? user.lastLogout : '';
        newTotalHours = user.totalHours || 0;
        newTotalTimeStr = user.totalTimeStr || '';
      }
      
      return {
        ...user,
        sessions: filteredSessions,
        firstLogin: newFirstLogin,
        lastLogout: newLastLogout,
        totalHours: newTotalHours,
        totalTimeStr: newTotalTimeStr
      };
    }).filter(user => user !== null); // Удаляем null (пользователей без данных в диапазоне)
    
    if (localReportSearchTerm.trim()) {
      return filtered.filter((user) => matchesLocalReportSearch(user, localReportSearchTerm, resolveFio));
    }

    return filtered;
  }, [localReportUsers, localReportSearchTerm, localReportStartDate, localReportEndDate, resolveFio, deletedLoginsLower]);

  // Кто из списка сотрудников отсутствует в текущем отчёте (за выбранный период)
  const absentInReport = useMemo(() => {
    const presentLower = new Set((filteredLocalReportUsers || []).map((r) => (r.fio || '').toLowerCase()));
    return EXPECTED_EMPLOYEES_FOR_ATTENDANCE.filter((latin) => {
      const key = (latin || '').toLowerCase();
      const alias = fioAliasesMap[latin];
      if (alias && alias.hide_in_absent) return false;
      if (deletedLoginsLower.has(key)) return false;
      return !presentLower.has(key);
    });
  }, [filteredLocalReportUsers, fioAliasesMap, deletedLoginsLower]);

  // Тип события календаря для отображения (как в Общем календаре)
  const getCalendarLeaveType = (leave) => {
    if (!leave) return 'leave';
    if (leave.type === 'vacation') return 'vacation';
    if (leave.type === 'sick') return 'sick';
    if (leave.type === 'remote') return 'remote';
    if (leave.type === 'leave') {
      const reason = String(leave.reason || '').toLowerCase();
      if (/болею|больнич/i.test(reason)) return 'sick';
      return 'leave';
    }
    return String(leave.type || 'leave');
  };

  // По данным календаря: кто и по какой причине отсутствует в выбранном периоде (usernameLower -> Set<type>)
  const calendarReasonsByUsername = useMemo(() => {
    const start = localReportStartDate || '';
    const end = localReportEndDate || '';
    if (!start || !end) return new Map();
    const map = new Map();
    const approved = (calendarLeaves || []).filter(l => (l.status === 'approved' || l.status === 'completed'));
    for (const leave of approved) {
      const leaveStart = String(leave.startDate || '').slice(0, 10);
      const leaveEnd = String(leave.endDate || '').slice(0, 10);
      if (!leaveStart || !leaveEnd) continue;
      if (leaveEnd < start || leaveStart > end) continue;
      const username = (calendarUsersById[leave.userId] || calendarUsersById[leave.user_id])?.username;
      if (!username) continue;
      const key = username.toLowerCase();
      const type = getCalendarLeaveType(leave);
      if (!map.has(key)) map.set(key, new Set());
      map.get(key).add(type);
    }
    return map;
  }, [calendarLeaves, calendarUsersById, localReportStartDate, localReportEndDate]);

  // Отправка системного сообщения Windows (msg *) на все RDP-сессии сервера
  const sendSystemMessage = async () => {
    const text = (systemMessageText || '').trim();
    if (!text) {
      setSystemMessageError('Введите текст сообщения');
      return;
    }
    setSystemMessageError(null);
    setSystemMessageSending(true);
    try {
      const token = localStorage.getItem('token');
      const isSendToAll = systemMessageTarget === 'all' || !systemMessageTarget;
      const target = isSendToAll ? null : String(systemMessageTarget);
      if (!isSendToAll && !target) {
        setSystemMessageError('Выберите пользователя из списка');
        return;
      }
      const sel = !isSendToAll ? windowsSessions.find((u) => {
        const v = typeof u === 'object' && u.sessionId != null ? String(u.sessionId) : (typeof u === 'object' ? u.username : u);
        return v === target;
      }) : null;
      const sessionId = typeof sel === 'object' && sel?.sessionId != null ? String(sel.sessionId) : (target && /^\d+$/.test(target) ? target : undefined);
      const sessionName = typeof sel === 'object' && sel?.sessionName ? String(sel.sessionName) : undefined;
      const payload = { message: text, sendToAll: isSendToAll };
      if (isSendToAll) {
        payload.target = null;
      } else {
        payload.target = target;
        if (sessionId) payload.sessionId = sessionId;
        if (sessionName) payload.sessionName = sessionName;
      }
      const res = await fetch(api('/api/send-system-message'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload)
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        setSystemMessageText('');
        setShowSystemMessageInput(false);
        alert(data.message || 'Сообщение отправлено.');
      } else {
        setSystemMessageError(data.error || 'Ошибка отправки');
      }
    } catch (e) {
      setSystemMessageError(e.message || 'Ошибка сети');
    } finally {
      setSystemMessageSending(false);
    }
  };

  // Открытие деталей пользователя: модалка открывается сразу, детали подгружаются в фоне.
  // Используем только выбранный диапазон дат (без расширения), чтобы не смешивать скриншоты/сайты/программы с соседними днями.
  const handleOpenLocalUserDetails = (user) => {
    const startDate = localReportStartDate || getTodayDate();
    const endDate = localReportEndDate || getTodayDate();
    // Сразу открываем модалку с базовыми данными (сессии, имя, даты)
    setLocalReportDetailsModal({
      open: true,
      logs: user.sessions || [],
      username: user.fio || user.username,
      realUsername: user.username,
      activityStats: null,
      urls: [],
      applications: [],
      screenshots: [],
      startDate,
      endDate
    });
    // Детали (сайты, приложения, скрины) подгружаем в фоне — только за выбранный период
    const params = new URLSearchParams({
      username: user.username,
      start: startDate,
      end: endDate
    });
    fetch(api(`/api/activity-details?${params.toString()}`))
      .then(res => res.json())
      .then(detailsData => {
        if (detailsData.success) {
          const apps = detailsData.applications || [];
          const firstAppTime = apps.length
            ? apps.reduce((minTs, app) => {
                const t = app.timestamp;
                if (!t) return minTs;
                return !minTs || (new Date(t).getTime() < new Date(minTs).getTime()) ? t : minTs;
              }, null)
            : null;
          const lastAppTime = apps.length
            ? apps.reduce((maxTs, app) => {
                const t = app.timestamp;
                if (!t) return maxTs;
                return !maxTs || (new Date(t).getTime() > new Date(maxTs).getTime()) ? t : maxTs;
              }, null)
            : null;
          const key = `${user.username}-${startDate}-${endDate}`;
          if (firstAppTime) {
            setFirstLoginFromApplications(prev => ({ ...prev, [key]: firstAppTime }));
          }
          if (lastAppTime) {
            setLastLogoutFromApplications(prev => ({ ...prev, [key]: lastAppTime }));
          }
          setDetailsAvailability(prev => ({
            ...prev,
            [key]: {
              hasApplications: (apps || []).length > 0,
              hasScreenshots: (detailsData.screenshots || []).length > 0
            }
          }));
          setLocalReportDetailsModal(prev => prev.open && prev.realUsername === user.username ? {
            ...prev,
            activityStats: detailsData.activityStats || null,
            urls: detailsData.urls || [],
            applications: apps,
            screenshots: detailsData.screenshots || []
          } : prev);
        }
      })
      .catch(() => {});
  };

  // Параметры открытой модалки «Подробнее» (для опроса без пересоздания callback)
  const detailsModalParamsRef = React.useRef({ open: false, realUsername: '', startDate: '', endDate: '' });
  detailsModalParamsRef.current = {
    open: localReportDetailsModal.open,
    realUsername: localReportDetailsModal.realUsername || '',
    startDate: localReportDetailsModal.startDate || '',
    endDate: localReportDetailsModal.endDate || ''
  };

  // Обновление данных в модалке «Подробнее» (сайты, скрины, приложения) — не чаще раза в 30 сек
  useEffect(() => {
    if (!localReportDetailsModal.open) return;
    const refresh = async () => {
      const p = detailsModalParamsRef.current;
      if (!p.open || !p.realUsername || !p.startDate || !p.endDate) return;
      try {
        const params = new URLSearchParams({ username: p.realUsername, start: p.startDate, end: p.endDate });
        const res = await fetch(api(`/api/activity-details?${params.toString()}`));
        const data = await res.json();
        if (res.ok && data.success) {
          const apps = data.applications || [];
          const firstAppTime = apps.length
            ? apps.reduce((minTs, app) => {
                const t = app.timestamp;
                if (!t) return minTs;
                return !minTs || (new Date(t).getTime() < new Date(minTs).getTime()) ? t : minTs;
              }, null)
            : null;
          const lastAppTime = apps.length
            ? apps.reduce((maxTs, app) => {
                const t = app.timestamp;
                if (!t) return maxTs;
                return !maxTs || (new Date(t).getTime() > new Date(maxTs).getTime()) ? t : maxTs;
              }, null)
            : null;
          if (firstAppTime && p.realUsername && p.startDate && p.endDate) {
            const key = `${p.realUsername}-${p.startDate}-${p.endDate}`;
            setFirstLoginFromApplications(prev => ({ ...prev, [key]: firstAppTime }));
          }
          if (lastAppTime && p.realUsername && p.startDate && p.endDate) {
            const key = `${p.realUsername}-${p.startDate}-${p.endDate}`;
            setLastLogoutFromApplications(prev => ({ ...prev, [key]: lastAppTime }));
          }
          if (p.realUsername && p.startDate && p.endDate) {
            const key = `${p.realUsername}-${p.startDate}-${p.endDate}`;
            setDetailsAvailability(prev => ({
              ...prev,
              [key]: {
                hasApplications: (apps || []).length > 0,
                hasScreenshots: (data.screenshots || []).length > 0
              }
            }));
          }
          setLocalReportDetailsModal(prev => ({
            ...prev,
            // Не затирать имеющиеся данные пустым ответом (при таймауте/ошибке ответ может прийти без массивов)
            urls: (data.urls && data.urls.length > 0) ? data.urls : (prev.urls?.length ? prev.urls : (data.urls || [])),
            applications: (apps && apps.length > 0) ? apps : (prev.applications?.length ? prev.applications : (apps || [])),
            screenshots: (data.screenshots && data.screenshots.length > 0) ? data.screenshots : (prev.screenshots?.length ? prev.screenshots : (data.screenshots || [])),
            activityStats: data.activityStats ?? prev.activityStats
          }));
        }
      } catch (e) { /* ignore */ }
    };
    refresh();
    const interval = setInterval(refresh, 30000);
    return () => clearInterval(interval);
  }, [localReportDetailsModal.open]);

  // Лёгкая мета для иконок/первого входа — один пакетный запрос вместо N тяжёлых
  const localReportFetchingKeysRef = React.useRef(new Set());
  // Раз в минуту перепроверяем тех, у кого нет скринов/приложений: данные могли прийти после открытия отчёта
  // (раньше «нет данных», полученное утром, держалось до перезагрузки страницы — красные иконки при наличии данных)
  const [metaRefreshTick, setMetaRefreshTick] = useState(0);
  useEffect(() => {
    if (!showLocalReport) return;
    const id = setInterval(() => setMetaRefreshTick((t) => t + 1), 60000);
    return () => clearInterval(id);
  }, [showLocalReport]);
  useEffect(() => {
    if (!showLocalReport || !localReportStartDate || !localReportEndDate) return;
    const start = localReportStartDate;
    const end = localReportEndDate;
    const need = filteredLocalReportUsers.filter((user) => {
      if (!String(user.username || '').trim()) return false;
      const key = `${user.username}-${start}-${end}`;
      const avail = detailsAvailability[key];
      if (avail && ((avail.hasApplications && avail.hasScreenshots) || avail.tick === metaRefreshTick)) return false;
      if (localReportFetchingKeysRef.current.has(key)) return false;
      return true;
    });
    if (!need.length) return;
    need.forEach((user) => localReportFetchingKeysRef.current.add(`${user.username}-${start}-${end}`));
    const params = new URLSearchParams({
      start,
      end,
      usernames: need.map((u) => u.username).filter(Boolean).join(',')
    });
    fetch(api(`/api/activity-details-meta?${params.toString()}`))
      .then((res) => res.json())
      .then((data) => {
        // Ошибка сервера — не считаем «данных нет» (иначе красные иконки у всех), повторим на следующем тике
        if (!data || !data.success || !data.byUser) return;
        const byUser = data.byUser;
        setDetailsAvailability((prev) => {
          const next = { ...prev };
          need.forEach((user) => {
            const key = `${user.username}-${start}-${end}`;
            const meta = byUser[String(user.username).trim()];
            if (!meta) return;
            next[key] = {
              hasApplications: !!meta.hasApplications,
              hasScreenshots: !!meta.hasScreenshots,
              tick: metaRefreshTick
            };
          });
          return next;
        });
        setLastLogoutFromApplications((prev) => {
          const next = { ...prev };
          need.forEach((user) => {
            const meta = byUser[user.username];
            if (meta?.lastAppTime) next[`${user.username}-${start}-${end}`] = meta.lastAppTime;
          });
          return next;
        });
        setFirstLoginFromApplications((prev) => {
          const next = { ...prev };
          need.forEach((user) => {
            const meta = byUser[user.username];
            if (meta?.firstAppTime) next[`${user.username}-${start}-${end}`] = meta.firstAppTime;
          });
          return next;
        });
      })
      .catch(() => {})
      .finally(() => {
        need.forEach((user) => localReportFetchingKeysRef.current.delete(`${user.username}-${start}-${end}`));
      });
  }, [showLocalReport, filteredLocalReportUsers, localReportStartDate, localReportEndDate, detailsAvailability, metaRefreshTick]);

  function formatWorkTime(mins) {
    if (mins === 0) return '0м';
    if (!mins || mins < 0) return '—';
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return `${h > 0 ? h + 'ч ' : ''}${m}м`;
  }

  // Переработка: минуты вне рабочего дня 9:00–18:00 (до 9:00 и после 18:00 по каждому календарному дню)
  function getOvertimeMinutes(firstLogin, lastLogout, workStartHour = 9, workEndHour = 18) {
    const start = new Date(firstLogin);
    const end = new Date(lastLogout);
    if (end <= start) return 0;
    let overtime = 0;
    const cur = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    const endDay = new Date(end.getFullYear(), end.getMonth(), end.getDate());
    while (cur <= endDay) {
      const workStart = new Date(cur.getFullYear(), cur.getMonth(), cur.getDate(), workStartHour, 0, 0, 0);
      const workEnd = new Date(cur.getFullYear(), cur.getMonth(), cur.getDate(), workEndHour, 0, 0, 0);
      const dayEnd = new Date(cur.getFullYear(), cur.getMonth(), cur.getDate(), 23, 59, 59, 999);
      const sessionOnDayStart = start > cur ? start : cur;
      const sessionOnDayEnd = end < dayEnd ? end : dayEnd;
      if (sessionOnDayStart < sessionOnDayEnd) {
        if (sessionOnDayStart < workStart) {
          const earlyEnd = workStart < sessionOnDayEnd ? workStart : sessionOnDayEnd;
          overtime += (earlyEnd.getTime() - sessionOnDayStart.getTime()) / 60000;
        }
        if (sessionOnDayEnd > workEnd) {
          const lateStart = workEnd > sessionOnDayStart ? workEnd : sessionOnDayStart;
          overtime += (sessionOnDayEnd.getTime() - lateStart.getTime()) / 60000;
        }
      }
      cur.setDate(cur.getDate() + 1);
    }
    return Math.round(overtime);
  }

  // Стили из эталонного кода
  // Часы в шапке отчёта — тикают только пока отчёт открыт
  useEffect(() => {
    if (!showLocalReport) return undefined;
    setNowTick(new Date());
    const id = setInterval(() => setNowTick(new Date()), 1000);
    return () => clearInterval(id);
  }, [showLocalReport]);

  const isSingleDayReport = localReportStartDate === localReportEndDate;

  const formatCellTime = useCallback((dtStr) => {
    if (!dtStr) return '—';
    const d = parseDate(dtStr);
    if (!d || Number.isNaN(d.getTime())) return '—';
    return isSingleDayReport
      ? d.toLocaleTimeString('ru-RU')
      : `${d.toLocaleDateString('ru-RU')}, ${d.toLocaleTimeString('ru-RU')}`;
  }, [isSingleDayReport]);

  // Строки таблицы с уже посчитанными временем/переработкой — нужно для сортировки и статистики
  const localReportRows = useMemo(() => {
    return (filteredLocalReportUsers || []).map((row, idx) => {
      const key = `${row.username}-${localReportStartDate || ''}-${localReportEndDate || ''}`;
      const first = firstLoginFromApplications[key] || null;
      const last = lastLogoutFromApplications[key] || null;
      let worked = 0;
      let overtime = 0;
      if (first && last) {
        const startMs = new Date(first).getTime();
        const endMs = new Date(last).getTime();
        worked = Math.max(0, Math.round((endMs - startMs) / 60000));
        overtime = Math.round(getOvertimeMinutes(first, last));
      }
      return {
        row,
        key,
        i: idx + 1,
        name: resolveFio(row.fio),
        first,
        last,
        firstMs: first ? new Date(first).getTime() : 0,
        lastMs: last ? new Date(last).getTime() : 0,
        worked,
        overtime,
        hasData: Boolean(first && last),
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredLocalReportUsers, firstLoginFromApplications, lastLogoutFromApplications, localReportStartDate, localReportEndDate, resolveFio]);

  const sortedLocalReportRows = useMemo(() => {
    const { k, d } = localReportSort;
    const numericKey = { i: 'i', first: 'firstMs', last: 'lastMs', worked: 'worked' }[k];
    const sorted = [...localReportRows].sort((x, y) => {
      if (k === 'name') return x.name.localeCompare(y.name, 'ru') * d;
      return ((x[numericKey] || 0) - (y[numericKey] || 0)) * d;
    });
    if (!hideFioDuplicates) {
      return sorted.map((item, idx) => ({ ...item, i: idx + 1 }));
    }
    return hideDuplicateFioRows(sorted, detailsAvailability);
  }, [localReportRows, localReportSort, hideFioDuplicates, detailsAvailability]);

  const fioDuplicateCount = useMemo(
    () => countHiddenFioDuplicates(localReportRows),
    [localReportRows]
  );

  const localReportStats = useMemo(() => {
    const rows = sortedLocalReportRows;
    const online = rows.filter((r) => r.worked > 0);
    const sum = online.reduce((s, r) => s + r.worked, 0);
    return {
      total: rows.length,
      online: online.length,
      avg: online.length ? Math.round(sum / online.length) : 0,
      overtime: rows.reduce((s, r) => s + r.overtime, 0),
    };
  }, [sortedLocalReportRows]);

  const toggleLocalReportSort = useCallback((k) => {
    setLocalReportSort((prev) => (prev.k === k ? { k, d: -prev.d } : { k, d: 1 }));
  }, []);

  const localReportPeriodLabel = useMemo(() => {
    if (!localReportStartDate) return '';
    if (isSingleDayReport) return ndFormatYmd(localReportStartDate);
    return `${ndFormatYmd(localReportStartDate)} — ${ndFormatYmd(localReportEndDate)}`;
  }, [localReportStartDate, localReportEndDate, isSingleDayReport]);

  const modalStyles = {
    overlay: {
      backgroundColor: 'rgba(0,0,0,0.45)',
      zIndex: 100001,
      position: 'fixed',
      top: 0,
      right: 0,
      bottom: 0,
      left: '320px',
      width: 'calc(100vw - 320px)',
      height: '100vh',
      margin: 0,
      padding: 0,
      display: 'flex',
      alignItems: 'stretch',
      justifyContent: 'flex-start',
      pointerEvents: 'auto'
    },
    content: {
      position: 'fixed',
      top: 0,
      left: '320px',
      right: 0,
      bottom: 0,
      width: 'calc(100vw - 320px)',
      maxWidth: 'calc(100vw - 320px)',
      height: '100vh',
      maxHeight: '100vh',
      margin: 0,
      padding: 0,
      inset: 'unset',
      border: 'none',
      background: 'transparent',
      overflow: 'hidden',
      zIndex: 100001,
      display: 'flex',
      alignItems: 'stretch',
      justifyContent: 'flex-start',
      transform: 'none !important',
      pointerEvents: 'auto',
      boxSizing: 'border-box'
    }
  };

  // Добавляем глобальные стили для overlay react-modal, чтобы он не закрывал сайдбар
  useEffect(() => {
    if (isOpen) {
      const styleId = 'worktime-modal-overlay-styles';
      // Удаляем старый стиль, если есть
      const existingStyle = document.getElementById(styleId);
      if (existingStyle) {
        existingStyle.remove();
      }
      
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        .ReactModal__Overlay--worktime {
          left: 320px !important;
          width: calc(100vw - 320px) !important;
          right: 0 !important;
          top: 0 !important;
          bottom: 0 !important;
          height: 100vh !important;
          position: fixed !important;
          pointer-events: auto !important;
          z-index: 100001 !important;
          background-color: rgba(0,0,0,0.45) !important;
          display: flex !important;
          align-items: stretch !important;
          justify-content: flex-start !important;
          margin: 0 !important;
          padding: 0 !important;
        }
        .ReactModal__Overlay--worktime .ReactModal__Content {
          left: 320px !important;
          width: calc(100vw - 320px) !important;
          max-width: calc(100vw - 320px) !important;
          min-width: 0 !important;
          height: 100vh !important;
          top: 0 !important;
          right: 0 !important;
          bottom: 0 !important;
          margin: 0 !important;
          padding: 0 !important;
          transform: none !important;
          pointer-events: auto !important;
          z-index: 100002 !important;
          box-sizing: border-box !important;
          overflow: hidden !important;
        }
        .ReactModal__Overlay--worktime .ReactModal__Content > div {
          width: 100% !important;
          max-width: 100% !important;
          min-width: 0 !important;
          box-sizing: border-box !important;
          overflow: hidden !important;
        }
        .ReactModal__Overlay--worktime * {
          pointer-events: auto !important;
        }
        .nd-tableWrap tbody tr:hover td .nd-whoName {
          color: var(--table-cell-hover-text-color, #ffffff) !important;
          -webkit-text-fill-color: var(--table-cell-hover-text-color, #ffffff) !important;
        }
      `;
      // Вставляем в начало head для максимального приоритета
      if (document.head.firstChild) {
        document.head.insertBefore(style, document.head.firstChild);
      } else {
        document.head.appendChild(style);
      }

      // Прямая DOM-манипуляция для принудительного применения стилей
      const applyStyles = () => {
        const overlays = document.querySelectorAll('.ReactModal__Overlay--worktime');
        overlays.forEach(overlay => {
          const s = overlay.style;
          s.setProperty('left', '320px', 'important');
          s.setProperty('width', 'calc(100vw - 320px)', 'important');
          s.setProperty('right', '0', 'important');
          s.setProperty('top', '0', 'important');
          s.setProperty('bottom', '0', 'important');
          s.setProperty('height', '100vh', 'important');
          s.setProperty('position', 'fixed', 'important');
          s.setProperty('background-color', 'rgba(0,0,0,0.45)', 'important');
          s.setProperty('display', 'flex', 'important');
          s.setProperty('align-items', 'stretch', 'important');
          s.setProperty('justify-content', 'flex-start', 'important');
          s.setProperty('margin', '0', 'important');
          s.setProperty('padding', '0', 'important');
          
          const content = overlay.querySelector('.ReactModal__Content');
          if (content) {
            const cs = content.style;
            cs.setProperty('left', '320px', 'important');
            cs.setProperty('width', 'calc(100vw - 320px)', 'important');
            cs.setProperty('max-width', 'calc(100vw - 320px)', 'important');
            cs.setProperty('min-width', '0', 'important');
            cs.setProperty('height', '100vh', 'important');
            cs.setProperty('top', '0', 'important');
            cs.setProperty('right', '0', 'important');
            cs.setProperty('bottom', '0', 'important');
            cs.setProperty('margin', '0', 'important');
            cs.setProperty('padding', '0', 'important');
            cs.setProperty('transform', 'none', 'important');
            cs.setProperty('box-sizing', 'border-box', 'important');
            cs.setProperty('overflow', 'hidden', 'important');
            
            // Применяем стили к внутренним div
            const firstDiv = content.querySelector('div');
            if (firstDiv) {
              firstDiv.style.setProperty('width', '100%', 'important');
              firstDiv.style.setProperty('max-width', '100%', 'important');
              firstDiv.style.setProperty('min-width', '0', 'important');
              firstDiv.style.setProperty('box-sizing', 'border-box', 'important');
              firstDiv.style.setProperty('overflow', 'hidden', 'important');
            }
          }
        });
      };

      // Применяем стили с задержкой, чтобы модалка успела отрендериться
      setTimeout(applyStyles, 0);
      setTimeout(applyStyles, 10);
      setTimeout(applyStyles, 50);
      setTimeout(applyStyles, 100);
      setTimeout(applyStyles, 200);

      // Наблюдаем за изменениями DOM и применяем стили
      const observer = new MutationObserver(applyStyles);
      observer.observe(document.body, {
        childList: true,
        subtree: true
      });
      
      return () => {
        const styleToRemove = document.getElementById(styleId);
        if (styleToRemove) {
          styleToRemove.remove();
        }
        observer.disconnect();
      };
    }
  }, [isOpen]);

  return (
    <Modal
      isOpen={actuallyOpen}
      onRequestClose={handleRequestClose}
      contentLabel="Мониторинг времени"
      style={modalStyles}
      ariaHideApp={false}
      overlayClassName="ReactModal__Overlay--worktime"
    >
      <div onClick={handleRequestClose} style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'stretch', justifyContent: 'flex-start', overflow: 'hidden', boxSizing: 'border-box' }}>
        <div onClick={e => e.stopPropagation()} style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
          overflow: 'hidden',
        }}>
          {!showAppUsage && !showRemoteWorktime && !showLocalReport && !showOneCHistoryModal && (
            <WorkTimeHub
              onClose={handleRequestClose}
              onOpenLaunch={() => setShowAppUsage(true)}
              onOpenRemote={() => setShowRemoteWorktime(true)}
              onOpenLocal={handleOpenLocalReport}
              onOpenOneC={() => setShowOneCHistoryModal(true)}
            />
          )}

          <AppUsageModal isOpen={showAppUsage} onRequestClose={() => setShowAppUsage(false)} />
          {console.log('🔍 [WorkTimeReportModal] Рендер RemoteWorktimeReportModal, showRemoteWorktime =', showRemoteWorktime)}
          {showRemoteWorktime && (
            <RemoteWorktimeReportModal 
              key="remote-worktime-modal"
              isOpen={showRemoteWorktime} 
              onRequestClose={() => {
                console.log('🔴 [WorkTimeReportModal] Закрываем RemoteWorktimeReportModal');
                setShowRemoteWorktime(false);
              }} 
            />
          )}
          {/* Модалка для локального отчета */}
          {console.log('🔍 [WorkTimeReportModal] Рендер модалки локального отчета, showLocalReport =', showLocalReport)}
          <Modal
            isOpen={showLocalReport}
            onRequestClose={() => {
              console.log('🔴 [WorkTimeReportModal] Закрываем модалку локального отчета');
              setShowLocalReport(false);
              setLocalReportUsers([]);
              setLocalReportSearchTerm('');
            }}
            contentLabel="Отчет активности локальных ПК"
            style={modalStyles}
            ariaHideApp={false}
            overlayClassName="ReactModal__Overlay--worktime"
          >
            <div onClick={(e) => e.stopPropagation()} className="nd-report">
              <div className="nd-topbar">
                <h1>
                  Отчет активности локальных ПК
                  <span>мониторинг рабочего времени · реальное время</span>
                </h1>

                <div className="nd-clockPill">
                  <span className="nd-t">{nowTick.toLocaleTimeString('ru-RU')}</span>
                  <i />
                  <span className="nd-d">
                    {(() => {
                      const wd = nowTick.toLocaleDateString('ru-RU', { weekday: 'long' });
                      return wd.charAt(0).toUpperCase() + wd.slice(1);
                    })()}
                  </span>
                  <i />
                  <span className="nd-d">{nowTick.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' })}</span>
                </div>

                <button
                  type="button"
                  className="nd-themeBtn"
                  onClick={toggleNdTheme}
                  title={ndIsLight ? 'Включить тёмную тему' : 'Включить светлую тему'}
                >
                  {ndIsLight ? '🌙' : '☀️'}
                </button>

                <button
                  type="button"
                  className="nd-closeBtn"
                  onClick={() => {
                    setShowLocalReport(false);
                    setLocalReportUsers([]);
                    setLocalReportSearchTerm('');
                  }}
                  title="Закрыть"
                  aria-label="Закрыть"
                >
                  ✕
                </button>
              </div>

              <div className="nd-content">

                <div className="nd-ctrlRow">
                  <div className="nd-dateNav">
                    <button
                      type="button"
                      className="nd-dBtn"
                      onClick={handleLocalReportPreviousDay}
                      title="Предыдущий период"
                    >
                      <FiChevronLeft size={18} />
                    </button>

                    <input
                      type="date"
                      className="nd-dateLbl"
                      value={localReportCurrentDate}
                      max={getTodayDate()}
                      title={localReportPeriodLabel}
                      onChange={(e) => {
                        const newDate = e.target.value;
                        if (!newDate) return;
                        if (newDate > getTodayDate()) {
                          alert('Нельзя выбрать будущую дату');
                          return;
                        }
                        applyLocalReportPeriod(newDate, localReportRange);
                      }}
                    />

                    <button
                      type="button"
                      className="nd-dBtn"
                      onClick={handleLocalReportNextDay}
                      title="Следующий период"
                    >
                      <FiChevronRight size={18} />
                    </button>
                  </div>

                  <div className="nd-rangeChips">
                    {[{ k: 'day', l: 'Сегодня' }, { k: 'week', l: 'Неделя' }, { k: 'month', l: 'Месяц' }].map((chip) => (
                      <button
                        key={chip.k}
                        type="button"
                        className={localReportRange === chip.k ? 'nd-rChip nd-on' : 'nd-rChip'}
                        onClick={() => {
                          setLocalReportRange(chip.k);
                          applyLocalReportPeriod(localReportCurrentDate, chip.k);
                        }}
                      >
                        {chip.l}
                      </button>
                    ))}
                  </div>

                  {!isSingleDayReport && (
                    <span className="nd-mono" style={{ fontSize: 11, fontWeight: 700, color: 'var(--nd-mut)' }}>
                      {localReportPeriodLabel}
                    </span>
                  )}

                  <div className="nd-searchW">
                    <input
                      type="text"
                      placeholder="Поиск по имени…"
                      value={localReportSearchTerm}
                      onChange={handleLocalReportSearchChange}
                      onFocus={() => setLocalReportShowAutocomplete(true)}
                      onBlur={() => setTimeout(() => setLocalReportShowAutocomplete(false), 150)}
                    />
                    {localReportShowAutocomplete && localReportUserOptions && localReportUserOptions.length > 0 && (
                      <div className="nd-autocomplete">
                        {localReportUserOptions.map((u, i) => (
                          <div
                            key={u + i}
                            onMouseDown={() => {
                              setLocalReportSearchTerm(u);
                              setLocalReportShowAutocomplete(false);
                            }}
                          >
                            {u}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="nd-statsRow">
                  <div
                    className="nd-statCard nd-glass"
                    style={{ '--nd-sc': 'var(--nd-green)', animationDelay: '0ms', cursor: 'pointer' }}
                    onClick={() => setLocalReportTab('online')}
                    title="Показать вкладку «Онлайн»"
                  >
                    <div className="nd-stIco">🟢</div>
                    <div>
                      <div className="nd-stV">{localReportStats.online}</div>
                      <div className="nd-stL">На связи из {localReportStats.total}</div>
                    </div>
                  </div>
                  <div className="nd-statCard nd-glass" style={{ '--nd-sc': 'var(--nd-acc2)', animationDelay: '60ms' }}>
                    <div className="nd-stIco">⏱</div>
                    <div>
                      <div className="nd-stV">{localReportStats.avg > 0 ? formatWorkTime(localReportStats.avg) : '—'}</div>
                      <div className="nd-stL">Среднее время</div>
                    </div>
                  </div>
                  <div className="nd-statCard nd-glass" style={{ '--nd-sc': 'var(--nd-amber)', animationDelay: '120ms' }}>
                    <div className="nd-stIco">🔥</div>
                    <div>
                      <div className="nd-stV">{localReportStats.overtime > 0 ? formatWorkTime(localReportStats.overtime) : '—'}</div>
                      <div className="nd-stL">Переработка суммарно</div>
                    </div>
                  </div>
                  <div
                    className="nd-statCard nd-glass"
                    style={{ '--nd-sc': 'var(--nd-red)', animationDelay: '180ms', cursor: 'pointer' }}
                    onClick={() => setLocalReportTab('offline')}
                    title="Показать вкладку «Офлайн»"
                  >
                    <div className="nd-stIco">🚫</div>
                    <div>
                      <div className="nd-stV">{absentInReport.length}</div>
                      <div className="nd-stL">Отсутствуют</div>
                    </div>
                  </div>
                </div>

                <div className="nd-actRow">
                  <button
                    type="button"
                    className="nd-btn nd-go"
                    onClick={() => fetchLocalReport(true)}
                    disabled={loadingLocalReport}
                  >
                    📊 {loadingLocalReport ? 'Загрузка…' : 'Показать отчет'}
                  </button>

                  <button
                    type="button"
                    className={hideFioDuplicates ? 'nd-btn nd-go' : 'nd-btn nd-ghost'}
                    onClick={() => {
                      setHideFioDuplicates((v) => {
                        const next = !v;
                        try {
                          localStorage.setItem('local-report-hide-fio-dups', next ? '1' : '0');
                        } catch (_) {}
                        return next;
                      });
                    }}
                    title="Скрыть строки с одинаковым ФИО (разные Windows-логины одного человека). Данные не удаляются."
                  >
                    {hideFioDuplicates
                      ? `👤 Дубли скрыты${fioDuplicateCount ? ` (−${fioDuplicateCount})` : ''}`
                      : `👤 Скрыть дубли ФИО${fioDuplicateCount ? ` (${fioDuplicateCount})` : ''}`}
                  </button>

                  <button
                    type="button"
                    className="nd-btn nd-dangerBtn"
                    onClick={() => handleDeleteByDateRange(localReportStartDate, localReportEndDate)}
                    disabled={!localReportStartDate || !localReportEndDate}
                  >
                    🗑 Удалить за период
                  </button>

                  <button type="button" className="nd-btn nd-dangerBtn" onClick={() => handleDeleteByPeriod('day')}>
                    🗑 За сегодня
                  </button>

                  <button type="button" className="nd-btn nd-dangerBtn" onClick={() => handleDeleteByPeriod('week')}>
                    🗑 За неделю
                  </button>

                  <button type="button" className="nd-btn nd-dangerBtn" onClick={() => handleDeleteByPeriod('month')}>
                    🗑 За месяц
                  </button>

                  <button
                    type="button"
                    className={showSystemMessageInput ? 'nd-btn nd-ghost' : 'nd-btn nd-info'}
                    onClick={() => {
                      setShowSystemMessageInput(v => !v);
                      setSystemMessageError(null);
                      if (showSystemMessageInput) { setSystemMessageText(''); setSystemMessageTarget('all'); }
                    }}
                    title="Отправить системное сообщение на все RDP-сессии (как в диспетчере задач)"
                  >
                    📢 Системное сообщение
                  </button>
                </div>

                {editingAliasLogin && (
                  <div className="nd-panelBox">
                    <h5>Редактирование / удаление сотрудника для отчёта локальных ПК</h5>
                    <div className="nd-row">
                      <div className="nd-field">
                        <label>Логин (латиница)</label>
                        <input
                          type="text"
                          className="nd-input"
                          value={editingAliasLogin}
                          onChange={(e) => setEditingAliasLogin(e.target.value)}
                        />
                      </div>
                      <div className="nd-field" style={{ minWidth: 260 }}>
                        <label>ФИО (кириллица для отчёта)</label>
                        <input
                          type="text"
                          className="nd-input"
                          value={editingAliasDisplayName}
                          onChange={(e) => setEditingAliasDisplayName(e.target.value)}
                          placeholder="Например: Артем Кошечкин"
                        />
                      </div>
                      <label className="nd-check">
                        <input
                          type="checkbox"
                          checked={editingAliasHide}
                          onChange={(e) => setEditingAliasHide(e.target.checked)}
                        />
                        <span>Не показывать в «Отсутствуют в отчёте»</span>
                      </label>
                      <button type="button" className="nd-btn nd-go" onClick={saveAlias} disabled={fioAliasesLoading}>
                        {fioAliasesLoading ? 'Сохранение…' : 'Сохранить'}
                      </button>
                      <button
                        type="button"
                        className="nd-btn nd-ghost"
                        onClick={() => { setEditingAliasLogin(''); setEditingAliasDisplayName(''); setEditingAliasHide(false); setFioAliasesError(null); }}
                      >
                        Отмена
                      </button>
                      <button
                        type="button"
                        className="nd-btn nd-ghost"
                        style={{ color: 'var(--nd-red)', borderColor: 'var(--nd-red)' }}
                        onClick={() => setAliasDeleted(editingAliasLogin, true)}
                        disabled={fioAliasesLoading}
                        title="Убрать сотрудника из таблицы и из «Отсутствуют в отчёте». Данные активности сохраняются."
                      >
                        🗑 Удалить из отчёта
                      </button>
                    </div>
                    {fioAliasesError && <div className="nd-err" style={{ marginTop: 10 }}>{fioAliasesError}</div>}
                  </div>
                )}

                {deletedAliasLogins.length > 0 && (
                  <div className="nd-panelBox">
                    <button
                      type="button"
                      className="nd-btn nd-ghost"
                      onClick={() => setShowDeletedAliases((v) => !v)}
                    >
                      {showDeletedAliases ? '▾' : '▸'} Удалённые из отчёта ({deletedAliasLogins.length})
                    </button>
                    {showDeletedAliases && (
                      <div className="nd-chips" style={{ marginTop: 10 }}>
                        {deletedAliasLogins.map((login) => (
                          <span key={login} className="nd-aChip">
                            <span>{resolveFio(login)}</span>
                            <span style={{ opacity: 0.6 }}>({login})</span>
                            <button
                              type="button"
                              className="nd-aChipEdit"
                              onClick={() => setAliasDeleted(login, false)}
                              disabled={fioAliasesLoading}
                              title="Вернуть сотрудника в отчёт"
                            >
                              ↺
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                    {!editingAliasLogin && fioAliasesError && <div className="nd-err" style={{ marginTop: 10 }}>{fioAliasesError}</div>}
                  </div>
                )}

                {showSystemMessageInput && (
                  <div className="nd-panelBox">
                    <h5>Кому отправить</h5>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 12, flexWrap: 'wrap' }}>
                      <label className="nd-check">
                        <input type="radio" name="sysmsg_target" checked={systemMessageTarget === 'all'} onChange={() => setSystemMessageTarget('all')} />
                        <span>Всем сессиям</span>
                      </label>
                      <label className="nd-check">
                        <input type="radio" name="sysmsg_target" checked={systemMessageTarget !== 'all'} onChange={() => {
                          if (!windowsSessions.length) setSystemMessageTarget('');
                          else {
                            const u = windowsSessions[0];
                            const v = typeof u === 'object' && u.sessionId != null ? String(u.sessionId) : (typeof u === 'object' ? u.username : u);
                            setSystemMessageTarget(v);
                          }
                        }} />
                        <span>Конкретному пользователю</span>
                      </label>
                      {systemMessageTarget !== 'all' && (
                        <div ref={sessionsListRef} style={{ position: 'relative', minWidth: 280 }}>
                          <button
                            type="button"
                            onClick={() => {
                              fetchWindowsSessions();
                              setShowSessionsListOpen(v => !v);
                            }}
                            disabled={windowsSessionsLoading}
                            className="nd-input"
                            style={{ width: '100%', textAlign: 'left', cursor: windowsSessionsLoading ? 'wait' : 'pointer' }}
                          >
                            {windowsSessionsLoading ? 'Загрузка...' : (windowsSessions.length === 0 ? 'Нет сессий (запустите на Windows-сервере)' : (() => {
                              const sel = windowsSessions.find(u => {
                                const v = typeof u === 'object' && u.sessionId != null ? String(u.sessionId) : (typeof u === 'object' ? u.username : u);
                                return v === systemMessageTarget;
                              });
                              return sel ? formatSessionDisplay(sel) : '— Выберите пользователя —';
                            })())}
                          </button>
                          {showSessionsListOpen && windowsSessions.length > 0 && (
                            <div
                              style={{
                                position: 'absolute',
                                top: '100%',
                                left: 0,
                                right: 0,
                                marginTop: 4,
                                maxHeight: 280,
                                overflowY: 'auto',
                                minWidth: 280,
                                background: 'var(--nd-solid)',
                                border: '1px solid var(--nd-stroke)',
                                borderRadius: 12,
                                boxShadow: 'var(--nd-shadow)',
                                zIndex: 10000
                              }}
                            >
                              {windowsSessions.map((u, i) => {
                                const sessionId = typeof u === 'object' ? u.sessionId : null;
                                const value = sessionId != null ? String(sessionId) : (typeof u === 'object' ? u.username : u);
                                const display = formatSessionDisplay(u);
                                return (
                                  <div
                                    key={value + '-' + i}
                                    onClick={() => { setSystemMessageTarget(value); setShowSessionsListOpen(false); }}
                                    style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: i < windowsSessions.length - 1 ? '1px solid var(--nd-stroke)' : 'none' }}
                                    onMouseEnter={e => { e.currentTarget.style.background = 'var(--nd-panel2)'; }}
                                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                                  >
                                    {display}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <h5>Текст сообщения (кириллица поддерживается)</h5>
                    <textarea
                      value={systemMessageText}
                      onChange={e => setSystemMessageText(e.target.value)}
                      placeholder={systemMessageTarget === 'all' ? 'Введите сообщение для всех рабочих столов...' : (() => {
                          const sel = windowsSessions.find(u => {
                            const v = typeof u === 'object' && u.sessionId != null ? String(u.sessionId) : (typeof u === 'object' ? u.username : u);
                            return v === systemMessageTarget;
                          });
                          const name = sel ? formatSessionDisplay(sel) : (systemMessageTarget || 'пользователя');
                          return `Сообщение для ${name}...`;
                        })()}
                      rows={3}
                      style={{ maxWidth: 560, minHeight: 90 }}
                    />
                    {systemMessageError && <div className="nd-err" style={{ marginTop: 6 }}>{systemMessageError}</div>}
                    <div style={{ display: 'flex', gap: 10, marginTop: 12, flexWrap: 'wrap' }}>
                      <button type="button" className="nd-btn nd-go" onClick={sendSystemMessage} disabled={systemMessageSending}>
                        {systemMessageSending ? 'Отправка…' : (systemMessageTarget === 'all' ? '📢 Отправить на все сессии' : (() => {
                          const sel = windowsSessions.find(u => {
                            const v = typeof u === 'object' && u.sessionId != null ? String(u.sessionId) : (typeof u === 'object' ? u.username : u);
                            return v === systemMessageTarget;
                          });
                          const name = sel ? formatSessionDisplay(sel) : (systemMessageTarget || '');
                          return `Отправить пользователю ${name}`;
                        })())}
                      </button>
                      <button type="button" className="nd-btn nd-ghost" onClick={() => { setShowSystemMessageInput(false); setSystemMessageText(''); setSystemMessageError(null); setSystemMessageTarget('all'); }}>
                        Отмена
                      </button>
                    </div>
                  </div>
                )}

                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, gap: 14 }}>
              {loadingLocalReport ? (
                <div className="nd-empty">Загрузка…</div>
              ) : localReportError ? (
                <div className="nd-empty">
                  <p style={{ marginBottom: 16, color: 'var(--nd-red)' }}>{localReportError}</p>
                  <button type="button" className="nd-btn nd-go" style={{ display: 'inline-flex' }} onClick={() => fetchLocalReport()}>
                    Повторить
                  </button>
                </div>
              ) : (
                <>
                <div className="nd-rangeChips" style={{ alignSelf: 'flex-start', flex: 'none' }}>
                  {[
                    { k: 'online', l: `🟢 Онлайн (${filteredLocalReportUsers.length})` },
                    { k: 'offline', l: `🔴 Офлайн (${absentInReport.length})` },
                  ].map((tab) => (
                    <button
                      key={tab.k}
                      type="button"
                      className={localReportTab === tab.k ? 'nd-rChip nd-on' : 'nd-rChip'}
                      onClick={() => setLocalReportTab(tab.k)}
                    >
                      {tab.l}
                    </button>
                  ))}
                </div>
                {localReportTab === 'offline' ? (
                  absentInReport.length === 0 ? (
                    <div className="nd-empty">Отсутствующих нет</div>
                  ) : (
                  <div className="nd-absent nd-glass">
                    <div className="nd-chips">
                      {absentInReport.map(latin => {
                        const reasons = calendarReasonsByUsername.get((latin || '').toLowerCase());
                        const types = reasons ? Array.from(reasons) : [];
                        return (
                          <span key={latin} className="nd-aChip">
                            <i />
                            <span>{resolveFio(latin)}</span>
                            {types.includes('vacation') && <FaUmbrellaBeach style={{ color: 'var(--nd-green)' }} title="В отпуске" />}
                            {types.includes('sick') && <FaBed style={{ color: 'var(--nd-red)' }} title="На больничном" />}
                            {types.includes('leave') && <FaWalking style={{ color: 'var(--nd-acc2)' }} title="Отгул" />}
                            {types.includes('remote') && <FaHome style={{ color: '#a78bfa' }} title="Работа из дома" />}
                            <button
                              type="button"
                              className="nd-aChipEdit"
                              onClick={() => startEditAlias(latin)}
                              title="Переименовать, скрыть из «Отсутствуют» или удалить из отчёта"
                            >
                              ✎
                            </button>
                          </span>
                        );
                      })}
                    </div>
                  </div>
                  )
                ) : filteredLocalReportUsers.length === 0 ? (
                  <div className="nd-empty">Нет данных за выбранный период</div>
                ) : (
                <div className="nd-tableWrap nd-glass">
                  <table>
                    <thead>
                      <tr>
                        {[
                          { k: 'i', l: '№', w: 48 },
                          { k: 'name', l: 'ФИО' },
                          { k: 'first', l: 'Первый вход' },
                          { k: 'last', l: 'Последний выход' },
                          { k: 'worked', l: 'Отработано' },
                        ].map((col) => (
                          <th
                            key={col.k}
                            style={col.w ? { width: col.w } : undefined}
                            className={localReportSort.k === col.k ? 'nd-sorted' : undefined}
                            onClick={() => toggleLocalReportSort(col.k)}
                          >
                            {col.l}
                            <span className="nd-ar">{localReportSort.k === col.k ? (localReportSort.d === 1 ? '↑' : '↓') : '↕'}</span>
                          </th>
                        ))}
                        <th style={{ cursor: 'default' }}>Детали</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedLocalReportRows.map((item, idx) => {
                        const avatarColor = ND_AVATAR_COLORS[item.i % ND_AVATAR_COLORS.length];
                        const pct = Math.min(100, (item.worked / ND_WORK_DAY_MINUTES) * 100);
                        const avail = detailsAvailability[item.key];
                        const noTime = !item.first;
                        const noScreenshots = avail && !avail.hasScreenshots;
                        const noApplications = avail && !avail.hasApplications;
                        return (
                          <tr
                            key={item.key || idx}
                            style={{ animationDelay: `${Math.min(idx * 30, 400)}ms` }}
                            onMouseEnter={(e) => paintLocalReportFio(e.currentTarget, true)}
                            onMouseLeave={(e) => paintLocalReportFio(e.currentTarget, false)}
                          >
                            <td className="nd-numC">{item.i}</td>
                            <td title="Выделите и скопируйте (Ctrl+C)">
                              <div className="nd-whoC">
                                <span className="nd-wAv" style={{ background: avatarColor }}>{ndInitials(item.name)}</span>
                                <span className="nd-whoName" style={{ userSelect: 'text' }}>{item.name}</span>
                                <span className={item.worked > 0 ? 'nd-stDot nd-on' : 'nd-stDot nd-off'} />
                                {item.row.fio && (
                                  <button
                                    type="button"
                                    className="nd-aChipEdit"
                                    onClick={() => startEditAlias(item.row.fio)}
                                    title="Переименовать или удалить сотрудника из отчёта локальных ПК"
                                  >
                                    ✎
                                  </button>
                                )}
                              </div>
                            </td>
                            <td className="nd-tC">{formatCellTime(item.first)}</td>
                            <td className="nd-tC">{formatCellTime(item.last)}</td>
                            <td className="nd-workC">
                              <div className="nd-workTop">
                                <span>{item.hasData ? formatWorkTime(item.worked) : (item.row.totalTimeStr || '—')}</span>
                                {item.overtime > 0 && <span className="nd-ov">переработка: {formatWorkTime(item.overtime)}</span>}
                              </div>
                              <div className="nd-pBar">
                                <i className={item.overtime > 0 ? 'nd-over' : undefined} style={{ width: `${pct}%` }} />
                              </div>
                            </td>
                            <td>
                              <div style={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
                                <button type="button" className="nd-moreBtn" onClick={() => handleOpenLocalUserDetails(item.row)}>
                                  Подробнее
                                </button>
                                {(noTime || noScreenshots || noApplications) && (
                                  <span className="details-missing-icons-wrap" title="Нет данных: скриншоты, приложения, время входа">
                                    {noScreenshots && <span className="nd-missIco" title="Нет скриншотов"><FiCamera size={16} /></span>}
                                    {noApplications && <span className="nd-missIco" title="Нет приложений"><FiGrid size={16} /></span>}
                                    {noTime && <span className="nd-missIco" title="Нет времени входа"><FiClock size={16} /></span>}
                                  </span>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                )}
                </>
              )}
              </div>
              </div>
            </div>
          </Modal>

          {/* Модалка История 1С */}
          <OneCDocumentHistoryMobile
            open={showOneCHistoryModal}
            onClose={() => setShowOneCHistoryModal(false)}
            onOpenMobileSidebar={() => {}}
          />

          {/* Модалка деталей пользователя с вкладками */}
          {localReportDetailsModal.open && (
            <UserWorkTimeDetailsMobile
              open={localReportDetailsModal.open}
              onClose={() => setLocalReportDetailsModal({ ...localReportDetailsModal, open: false })}
              logs={localReportDetailsModal.logs}
              username={localReportDetailsModal.username}
              realUsername={localReportDetailsModal.realUsername}
              activityStats={localReportDetailsModal.activityStats}
              urls={localReportDetailsModal.urls}
              applications={localReportDetailsModal.applications}
              screenshots={localReportDetailsModal.screenshots}
              startDate={localReportDetailsModal.startDate}
              endDate={localReportDetailsModal.endDate}
            />
          )}
        </div>
      </div>
    </Modal>
  );
}

export default WorkTimeReportModal;